import React, { useMemo, useRef, useState, useEffect, useCallback } from 'react'
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Button,
    CircularProgress,
} from '@mui/material'
import { Auth } from 'aws-amplify'
import { useThemeStyling } from '../../hooks/useThemeStyling/useThemeStyling'
import {
    useAuthedUser,
    clearLocalStorage,
} from '../../context/AuthedUser/AuthedUserContext'
import idDirectory from './idAttributes'
import './SabreTermsAndConditionsModal.scss'
import { useLocalStorage } from 'hooks/useLocalStorage/useLocalStorage'

export interface TermsAndConditionsSection {
    id: string
    title: string
    content: React.ReactNode
}

export interface SabreTermsAndConditionsModalProps {
    onClose?: () => void
    onDecline?: () => void
    headerTitle?: string
    children?: React.ReactNode
    testId: string
    maxModalWidth?: 'lg' | 'md' | 'sm' | 'xl' | 'xs' | false
    disableBackdropClick?: boolean
    acceptBtnText?: string
    inactivateAcceptBtn?: boolean
    isLoadingAcceptBtn?: boolean
    showDeclineBtn?: boolean
    declineBtnText?: string
    inactivateDeclineBtn?: boolean
    isLoadingDeclineBtn?: boolean
    showCloseIcon?: boolean
    className?: string
    paperClassName?: string
}

const SabreTermsAndConditionsModal = ({
    onClose,
    onDecline,
    headerTitle = 'Terms and Conditions',
    children,
    testId,
    maxModalWidth = 'lg',
    disableBackdropClick = true,
    acceptBtnText = 'Accept Terms',
    inactivateAcceptBtn = false,
    isLoadingAcceptBtn = false,
    showDeclineBtn = true,
    declineBtnText = 'Decline',
    inactivateDeclineBtn = false,
    isLoadingDeclineBtn = false,
    className = '',
    paperClassName = '',
}: SabreTermsAndConditionsModalProps): React.ReactElement => {
    const { clientTheme } = useThemeStyling()
    const { user } = useAuthedUser()
    // Terms & Conditions modal state (always show for development)
    const [isTermsModalOpen, setIsTermsModalOpen] = useState(true)
    const handleAcceptTerms = () => setIsTermsModalOpen(false)

    const [tosDisabled, setTosDisabled] = useLocalStorage<boolean>(
        'tosDisabled',
        false
    )

    const sections = useMemo(
        () => [
            {
                id: 'overview',
                title: 'Overview & Acceptance',
                content: (
                    <>
                        <p>LAST UPDATED: 07/09/2025</p>
                        <p>
                            THIS SOFTWARE AND SERVICES AGREEMENT (THIS
                            “AGREEMENT”) IS ENTERED INTO BETWEEN SABRE GLBL INC.
                            (“COMPANY”) AND CUSTOMER (“CUSTOMER”) (DEFINED
                            HEREIN), GOVERNS CUSTOMER’S ACCESS TO, USE OF, AND
                            RECEIPT OF COMPANY’S SERVICES (DEFINED HEREIN) AND
                            IS EFFECTIVE AS OF THE DATE OF CUSTOMER’S ACCEPTANCE
                            ("EFFECTIVE DATE").
                        </p>
                        <p>
                            BY (1) INDICATING CUSTOMER’S ACCEPTANCE OF THIS
                            AGREEMENT, (2) EXECUTING A CHARGEBACK MANAGEMENT
                            SALES ORDER (S), OR (3) USING THE SERVICES
                            ("ACCEPTANCE"), CUSTOMER SIGNIFIES ACCEPTANCE OF
                            THIS AGREEMENT AND ACKNOWLEDGES THAT IT HAS READ,
                            UNDERSTANDS, AND WILL COMPLY WITH THE SAME. IF
                            CUSTOMER DOES NOT ACCEPT THIS AGREEMENT, THEN
                            CUSTOMER MUST NOT ACCESS OR USE THE SERVICES. AN
                            INDIVIDUAL ACCEPTING THIS AGREEMENT ON BEHALF OF AN
                            ENTITY REPRESENTS THAT SHE HAS THE AUTHORITY TO BIND
                            SUCH ENTITY AND ITS AFFILIATES TO THIS AGREEMENT
                            WHERE AND AS APPLICABLE.
                        </p>
                    </>
                ),
            },
            {
                id: 'definitions',
                title: '1. Definitions',
                content: (
                    <>
                        <ul>
                            <li>
                                "Acceptable Use Policy" means COMPANY’s
                                Acceptable Use Policy, incorporated herein by
                                reference and available at{' '}
                                <a
                                    href="/terms-of-use"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Terms of Use
                                </a>
                                .
                            </li>
                            <li>
                                “Affiliate” means any entity, directly or
                                indirectly, that now or hereafter is controlled
                                by, is under common control with, or controls
                                such party, where “control” means the direct or
                                indirect ownership of more than 50% of the
                                voting securities or other ownership interest of
                                an entity. Subject to this Agreement, Customer’s
                                Affiliates may use the license granted
                                hereunder.
                            </li>
                            <li>
                                “API” means the application programming
                                interface(s) provided by COMPANY that may permit
                                Customer to access the Subscription Services.
                            </li>
                            <li>
                                "AI Functionality" means the artificial
                                intelligence application features or
                                applications available in the Services which may
                                be accessed by Customer.
                            </li>
                            <li>
                                “APM (Alternative Payment Method)” means a
                                payment transaction method other than credit or
                                debit card, such as loyalty rewards or points,
                                or other non-card methods.
                            </li>
                            <li>
                                “Applicable Law” means all applicable laws,
                                rules, regulations and other binding
                                requirements of any regulator or other
                                governmental agency or entity, including Data
                                Protection Laws, with jurisdiction over the
                                Services, COMPANY or Customer, as applicable.
                            </li>
                            <li>
                                “Chargeback” means the formal incident and
                                process related to a Transaction Dispute
                                originating from a card transaction.
                            </li>
                            <li>
                                “Confidential Information” means all non-public
                                information disclosed by a party (“Disclosing
                                Party”) under this Agreement to the other party
                                (“Receiving Party”), whether orally or in
                                writing, that is designated as “confidential” at
                                the time of disclosure or that reasonably should
                                be understood to be confidential given the
                                nature of the information and/or the
                                circumstances of disclosure, including, without
                                limitation: the terms and conditions of the
                                Agreement (including pricing), business and
                                marketing plans, technology and technical
                                information, products and product plans and
                                designs, and business processes, trade secrets,
                                financial data, and know-how. For clarity, the
                                features, functionality and content of the
                                Services (including all data and information
                                made available by COMPANY via the Services), any
                                Documentation, and any information regarding
                                planned modifications or updates to the Services
                                or other COMPANY products and services,
                                constitute COMPANY Confidential Information.
                                Customer Confidential Information includes
                                Customer Personal Data, as defined under Data
                                Protection Laws.
                            </li>
                            <li>
                                “Customer” means the entity that is a party to
                                this Agreement, which may be a Merchant, as
                                applicable.
                            </li>
                            <li>
                                “Customer Data” means any data, information, or
                                material that Customer provides to and stores in
                                the Subscription Services, including Customer’s
                                End User data, or to COMPANY, whether provided
                                directly or indirectly through an authorized
                                Data Provider, for the performance of the
                                Agreement, which may be provided by Customer or
                                through one or more Data Providers.
                            </li>
                            <li>
                                “Data Protection Laws” has the meaning given to
                                it in the Data Processing Addendum.
                            </li>
                            <li>
                                “Data Processing Addendum” means the data
                                processing addendum executed by Customer and
                                COMPANY for the provision of the Services.
                            </li>
                            <li>
                                “Data Provider” means an entity contracted
                                directly or indirectly by Customer that performs
                                a data related function, controlled and
                                permitted by Customer.
                            </li>
                            <li>
                                “Documentation” means official technical user
                                documentation about the Services provided by
                                COMPANY. For the avoidance of doubt, any
                                publicly available site, videos, white papers,
                                marketing materials or related media, or related
                                feedback do not constitute Documentation.
                            </li>
                            <li>
                                “End User” means any Person that may interact
                                with the Services, including any Person,
                                Merchant, Customer or User of Customer
                                submitting data in relation to a Transaction
                                Dispute.
                            </li>
                            <li>
                                “Fees” means the applicable charges that
                                Customer is to pay to COMPANY, whether directly
                                or indirectly, for the Services as set forth in
                                the applicable Chargeback Management Sales
                                Order.
                            </li>
                            <li>
                                "Input" means any information that is provided
                                by Customer for the purpose of the Services
                                defined herein.
                            </li>
                            <li>
                                “Losses” means damages, losses, liabilities,
                                settlements, fines, penalties, judgments,
                                expenses and costs (including reasonable
                                attorneys’ fees) payable as a result of a Claim.
                            </li>
                            <li>
                                “Malicious Code” means any “back door,” “drop
                                dead device,” “time bomb,” “Trojan horse,”
                                “virus,” “ransomware,” “worm” or any other code
                                designed, intended to have, or capable of
                                disrupting, disabling, harming, interfering with
                                or otherwise impeding in any manner the
                                operation of, or providing unauthorized access
                                to, a computer system or network or other
                                device; or (b) damaging or destroying any data
                                or file.
                            </li>
                            <li>
                                “Merchant” means an Person that accepts payment
                                cards, which may be a Customer, as applicable.
                            </li>
                            <li>
                                "Output" means results generated and returned
                                based on Input.
                            </li>
                            <li>
                                “Payment Provider” means the entity for which a
                                payment account was provided to the Customer,
                                for the purpose of either issuing or acquiring a
                                payment.
                            </li>
                            <li>
                                “Person” means any individual, corporation,
                                firm, association, partnership, limited
                                liability company or other legal entity and any
                                government agency, body or authority.
                            </li>
                            <li>
                                “Personal Data” has the meaning given to it in
                                the Data Processing Addendum.
                            </li>
                            <li>
                                “Professional Services” means professional
                                services, such as implementation, consulting
                                services, and training, as expressly set forth
                                in an SOW.
                            </li>
                            <li>
                                “Chargeback Management Sales Order” means an
                                online order form, product information
                                dashboard, or other ordering document issued by
                                COMPANY that specifies the Services provided
                                hereunder along with pricing and other related
                                information (e.g., authorized End User, Term,
                                Usage Limits).
                            </li>
                            <li>
                                “Services” means Subscription Services,
                                value-added-services, additional add-on
                                components and software utilities, data
                                services, Professional Services and any other
                                products or services provided by COMPANY to the
                                Customer, including those defined in
                                Product-specific Terms.
                            </li>
                            <li>
                                “Subscription Service(s)” means COMPANY’s
                                SaaS-based services under this Agreement, which
                                may include the AI Functionality, and API made
                                available and provided by COMPANY to Customer.
                            </li>
                            <li>
                                “Systems and Applications” means applications,
                                software, hardware, databases, data warehouses,
                                network and communications equipment, websites,
                                and other products or services provided by
                                Customer or a third party.
                            </li>
                            <li>
                                “Test Data” means non-production data used to
                                test the COMPANY product that: (a) does not
                                contain Personal Data or (b) is aggregate and
                                anonymous data.
                            </li>
                            <li>
                                “Term” means the term of this Agreement, as
                                outlined in Section 6.
                            </li>
                            <li>
                                “Transaction Dispute” means the incident or
                                process, of a settled payment transaction
                                between two parties (typically an acquiring and
                                issuing bank), by which the payment is contested
                                by the payment originator, for the purpose of
                                recovering funds that were previously disbursed
                                to the recipient in exchange for a good or
                                service.
                            </li>
                            <li>
                                “Units” means the basis on which fees are
                                established for Services, as outlined in the
                                Chargeback Management Sales Order or
                                Documentation, as applicable.
                            </li>
                            <li>
                                “User” means a Customer, including an employee,
                                independent contractor, or agent who has a
                                business need to use the Services and
                                Documentation for or on behalf of Customer,
                                under this Agreement.
                            </li>
                        </ul>
                    </>
                ),
            },
            {
                id: 'grant',
                title: '2. Grant; No Fee Access',
                content: (
                    <>
                        <p>
                            2.1 Grant. Upon payment of the fees and subject to
                            Customer’s compliance with this Agreement, COMPANY
                            grants Customer a limited, revocable, non-exclusive,
                            non-transferable, non-sublicensable right during the
                            Term to internally: (i) access and use the
                            Subscription Services and Documentation for
                            Customer’s own internal business purposes and (ii)
                            access, display, and use the Documentation. Customer
                            may provide or make available to Customer’s
                            Affiliates and Users the Subscription Services or
                            Documentation.
                        </p>
                        <p>
                            2.2 No Fee Access. If Subscription Services are
                            provided to Customer for evaluation, beta, release
                            candidate, or free purposes, COMPANY hereby grants
                            to Customer, subject to Customer’s compliance with
                            this Agreement, a limited, revocable, non-exclusive,
                            non-transferable, non-sublicensable right to access
                            and use evaluation, beta, or release candidate
                            features and functionality or free versions of the
                            Subscription Services pursuant to the terms of the
                            Agreement and provided the Customer does not exceed
                            any usage limits pertaining thereto and agrees to
                            use Test Data only. Subscription Services provided
                            for evaluation, beta, or release candidate purposes
                            shall not be used for production use, and Customer’s
                            access and use right shall terminate on the end date
                            of the predetermined period or immediately upon
                            notice from COMPANY in its sole discretion,
                            whichever occurs first. CUSTOMER ACKNOWLEDGES AND
                            AGREES THAT THE SERVICES PROVIDED UNDER THIS SECTION
                            ARE PROVIDED ON AN “AS-IS” BASIS AND WITHOUT ANY
                            INDEMNIFICATION, SUPPORT, OR WARRANTIES OR
                            REPRESENTATIONS OF ANY KIND, WHETHER STATUTORY,
                            EXPRESS OR IMPLIED, AND COMPANY HEREBY DISCLAIMS AND
                            EXCLUDES ALL WARRANTIES, INCLUDING ANY IMPLIED
                            WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
                            PARTICULAR PURPOSE AND NON-INFRINGEMENT OF THIRD
                            PARTY RIGHTS. Except to the extent such terms
                            conflict with this Section, all other terms of this
                            Agreement shall apply to the Services provided for
                            evaluation, beta, release candidate, or free
                            purposes.
                        </p>
                    </>
                ),
            },
            {
                id: 'use',
                title: '3. Use of Services; Restrictions',
                content: (
                    <>
                        <p>
                            3.1 Use of Services. Customer acknowledges, agrees,
                            and warrants that it: (i) will be responsible for
                            its, its Users’ and End Users’ activity and use of
                            the Services in accordance with the this Agreement
                            and Applicable Laws and corresponding compliance
                            with this Agreement, and if Customer becomes aware
                            of any violation, Customer will immediately
                            terminate the offending party’s access to the
                            Services and promptly notify COMPANY; (ii) will only
                            utilize the Services as authorized for purposes
                            related to Transaction Dispute management, with
                            properly licensed Systems and Applications; (iii) is
                            legally able to process and legally provide Customer
                            Data and Input to COMPANY (including the means by
                            which Customer acquired Customer Data and Input,
                            such as obtaining required consents or rights under
                            Applicable Laws for such activities, whether
                            provided to COMPANY directly or indirectly through
                            an authorized Data Provider) and will be responsible
                            for the accuracy, security, quality, integrity, and
                            legality of the same; (iv) AI Functionality may
                            generate shared general information and it will
                            utilize all AI Functionality in accordance with
                            COMPANY's Acceptable Use Policy; and (v) is
                            responsible for, and will maintain, the security,
                            completeness, and accuracy of Customer’s
                            registration information, account, passwords, and
                            billing information (and promptly notify COMPANY if
                            it becomes aware of any unauthorized access or use
                            of the same or any other known or suspected breach
                            of security). COMPANY may suspend Customer’s access
                            to or use of the Services immediately if COMPANY
                            reasonably believes Customer’s use of the Services
                            may pose a security risk to or adversely impact the
                            Services and related systems and infrastructure or
                            create a risk of liability or harm to COMPANY or its
                            other customers or users. Some features of the
                            Services may utilize AI Functionality. If Customer
                            utilizes the AI Functionality, Customer hereby
                            instructs COMPANY to process Customer Data for such
                            purposes. Customer acknowledges and agrees it is
                            solely responsible for its use of any Output
                            generated by the AI Functionality, including
                            evaluating the accuracy of such Output. Customer
                            further acknowledges and agrees that Company may at
                            its sole discretion suspend any Service provided
                            hereunder in the event that payment is not made
                            within 10 days of the issuance of the associated
                            invoice.
                        </p>
                        <p>
                            3.2 Restrictions. Customer shall not (and shall not
                            permit its Users or End Users to), directly or
                            indirectly: (a) exceed its rights to use the
                            Services pursuant to this Agreement and as set forth
                            in the Chargeback Management Sales Order; (b) make
                            any Services available to anyone other than
                            Customer, Users, or End Users, or use any
                            Subscription Service for the benefit of anyone other
                            than Customer or its Affiliates, unless expressly
                            stated otherwise in a Chargeback Management Sales
                            Order; (c) sell, resell, license, sublicense,
                            distribute, rent, lease, loan, or otherwise transfer
                            any Services; (d) reverse engineer, decompile, or
                            disassemble, or otherwise obtain or attempt to
                            create, derive, or obtain the source code or
                            unauthorized access of the Services; (e) modify,
                            translate, enhance, or otherwise change the Services
                            or prepare derivative works of the Services,
                            including the creation and distribution of add-ons
                            or enhancements or incorporation independently or
                            into another product unless expressly agreed between
                            the parties; (f) use the Services to violate third
                            party rights or Applicable Laws or to process,
                            store, or transmit unauthorized, infringing,
                            libelous, unlawful, or tortious data, information,
                            or material that it is not authorized to process, or
                            data, information, or material in violation of
                            third-party rights; (g) use the Services to store or
                            transmit Malicious Code or interfere with, impair,
                            or disrupt the integrity or performance of the
                            Services, any other party’s use of the Services, or
                            third-party data contained therein; (h) use or
                            otherwise exploit the Services for the purposes of
                            benchmarking, competitive analysis, or the
                            development of a competing software product; (i)
                            scrape, export, store, or otherwise retain any
                            copies of data accessible through the Services; (j)
                            circumvent or attempt to circumvent any methods
                            employed by COMPANY to control access to the
                            components, features, or functions of the Services;
                            (k) use any automated means, including generative
                            artificial intelligence, agents, robots, scripts or
                            spiders, to disrupt the Subscription Services; (l)
                            use the Services to mislead any person that Output
                            generated was solely generated by a human or use the
                            Services to make automated decisions that may have a
                            detrimental impact on individual rights without
                            appropriate human oversight or supervision.
                        </p>
                    </>
                ),
            },
            {
                id: 'ownership',
                title: '4. Ownership; Feedback; Customer Data; Data Protection',
                content: (
                    <>
                        <p>
                            4.1 Ownership. COMPANY is the sole and exclusive
                            owner(s) of all right, title, and interest in and to
                            the Services and COMPANY’s proprietary property
                            (including all improvements, enhancements, or
                            modifications of the foregoing or any other
                            intellectual property or proprietary rights) and
                            reserves and retains all right, title and interest
                            in and to the same. Customer shall not alter,
                            obscure, or remove any intellectual property,
                            proprietary, or legal notice from the Services or
                            Documentation.
                        </p>
                        <p>
                            4.2 Feedback. Customer may from time to time provide
                            suggestions, comments, or other feedback to COMPANY
                            with respect to the Services (“Feedback”). Customer
                            grants to COMPANY a worldwide, royalty-free,
                            fully-paid, worldwide, transferable, sublicensable,
                            irrevocable and perpetual license to use and
                            incorporate the Feedback for any purpose.
                        </p>
                        <p>
                            4.3 Customer Data. As between Customer and COMPANY,
                            Customer is the sole and exclusive owner of all
                            right, title and interest in and to the Customer
                            Data and reserves all right, title and interest in
                            and to the Customer Data not expressly granted under
                            the Agreement. Customer will be solely responsible
                            for the procurement, accuracy, quality, integrity,
                            legality, and content of Customer Data and Input
                            ("Customer Submissions"). The Services are not
                            intended to be a data backup service, and Customer
                            shall not use the Services as a data backup service.
                            Customer grants COMPANY the right to host and
                            process Customer Submissions for providing and
                            improving the Services and to support Customer under
                            this Agreement and for the purposes described under
                            the Data Processing Addendum. If Customer chooses to
                            use Systems and Applications with the Subscription
                            Services, Customer grants COMPANY permission to
                            allow such Systems and Applications to access
                            Customer Data and information about Customer’s usage
                            of the Systems and Applications, as appropriate.
                            Unless as specifically set forth in a Chargeback
                            Management Sales Order, Customer expressly
                            acknowledges and agrees that it shall neither submit
                            to the Services, nor use the Services to process any
                            data or information that constitutes classified
                            information or Personal Data, and COMPANY shall not
                            be liable to Customers, Users, or End Users for any
                            such data or information.
                        </p>
                        <p>
                            4.4 Data Protection. 4.4.1 During the Term, each
                            party shall comply with its respective obligations
                            under Data Protection Laws, and each party shall
                            maintain commercially reasonable administrative,
                            technical, and physical safeguards designed to
                            protect Confidential Information and Customer Data
                            in its possession or under its control from
                            accidental loss and unauthorized access, use,
                            alteration, or disclosure. While COMPANY strives to
                            provide robust security, no system is completely
                            secure. COMPANY cannot guarantee that unauthorized
                            third parties will never be able to defeat our
                            security measures or misuse your Confidential
                            Information or Customer Data. We encourage you to
                            remain vigilant and take responsibility for securing
                            your personal data, knowing that you provide it at
                            your own risk. 4.4.2 To the extent applicable, the
                            parties shall process Personal Data in accordance
                            with COMPANY’s Data Processing Addendum, which is
                            incorporated into the Agreement by reference and
                            located here:{' '}
                            <a
                                href="/dpa"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Data Processing Addendum
                            </a>
                            . In case of a conflict between the provisions of
                            this Agreement and the Data Processing Addendum, the
                            provisions of the Data Processing Addendum will
                            prevail.
                        </p>
                    </>
                ),
            },
            {
                id: 'warranties',
                title: '5. Warranties; Disclaimers',
                content: (
                    <>
                        <p>
                            5.1 Warranties of the Parties. Each party represents
                            to the other party that it: (i) has validly entered
                            into this Agreement and has the legal power and
                            authority to do so; and (ii) shall comply in all
                            material respects with Applicable Laws and other
                            requirements to perform its obligations under this
                            Agreement.
                        </p>
                        <p>
                            5.2 Disclaimers. EXCEPT AS SPECIFICALLY SET FORTH IN
                            THIS SECTION 5, COMPANY MAKES NO REPRESENTATIONS OR
                            WARRANTIES, AND THE SERVICES ARE PROVIDED “AS IS”.
                            COMPANY EXPRESSLY DISCLAIMS AND EXCLUDES ALL OTHER
                            WARRANTIES, WHETHER STATUTORY, EXPRESS OR IMPLIED,
                            INCLUDING BUT NOT LIMITED TO, IMPLIED WARRANTY OF
                            MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
                            NON-INFRINGEMENT, TITLE, QUIET ENJOYMENT,
                            SATISFACTORY QUALITY AND THOSE ARISING FROM COURSE
                            OF PERFORMANCE, COURSE OF DEALING, OR USAGE OF
                            TRADE. COMPANY MAKES NO WARRANTY ABOUT THE ACCURACY,
                            SECURITY, LOSS OR CORRUPTION OF DATA, OR ABSENCE OF
                            DEFECT RELATING TO THE PRODUCTS AND SERVICES OR THE
                            RESULTS OF THE SAME. COMPANY DOES NOT WARRANT THAT
                            OPERATION OF THE SUBSCRIPTION SERVICES WILL BE
                            UNINTERRUPTED OR ERROR-FREE, AND GIVEN THE NATURE OF
                            DIGITAL DELIVERABILITY AND RELATED THIRD PARTY
                            DEPENDENCIES, COMPANY CANNOT GUARANTEE DELIVERY OR
                            RECEIPT. CUSTOMER ASSUMES ALL RESPONSIBILITY FOR
                            DETERMINING WHETHER THE SERVICES ARE ACCURATE AND
                            SUFFICIENT FOR CUSTOMER’S PURPOSES. CUSTOMER WILL
                            NOT PURSUE OR MAINTAIN ANY CLAIMS AGAINST COMPANY OR
                            A THIRD PARTY ARISING FROM OR RELATING TO ANY OUTPUT
                            GENERATED BY THE AI FUNCTIONALITY. COMPANY
                            SPECIFICALLY DISCLAIMS RESPONSIBILITY FOR SYSTEMS
                            AND APPLICATIONS WITH WHICH CUSTOMER MAY UTILIZE THE
                            SERVICES, AND CUSTOMER SPECIFICALLY DISCLAIMS AND
                            WAIVES ANY RIGHTS AND CLAIMS AGAINST COMPANY WITH
                            RESPECT TO SUCH SYSTEMS AND APPLICATIONS.
                        </p>
                    </>
                ),
            },
            {
                id: 'term',
                title: '6. Term and Termination',
                content: (
                    <>
                        <p>
                            6.1 Term of Agreement. This Agreement commences on
                            the Effective Date and, unless terminated sooner as
                            provided herein, will continue for the period
                            specified in the applicable Chargeback Management
                            Sales Order (“Initial Term”). Unless otherwise
                            agreed, the Agreement will automatically renew for
                            successive renewal terms upon the expiration of the
                            Initial Term and any renewal term thereafter (each a
                            “Renewal Term,” and collectively with the Initial
                            Term, the “Term”). Each Renewal Term will be the
                            same length as the Initial Term unless otherwise
                            specified by COMPANY at the time of renewal.
                        </p>
                        <p>
                            6.2 Termination. Customers with a monthly
                            subscription may terminate this Agreement by
                            providing COMPANY with prior notice of Customer’s
                            intention not to renew this Agreement prior to the
                            end of the applicable Term. If Customer has
                            subscribed to an annual or multi-year subscription
                            term, either party may terminate this Agreement by
                            providing the other party with thirty (30) days
                            prior written notice of their intention not to renew
                            the Agreement prior to the end of the applicable
                            Term and such termination will be effective at the
                            end of the then-current Term.
                        </p>
                        <p>
                            6.3 Termination for Breach. This Agreement may be
                            earlier terminated by either party (a) if the other
                            party materially breaches this Agreement and fails
                            to cure such breach within thirty (30) days or after
                            receiving written notice of such breach from the
                            other party, except the cure period for non-payment
                            is five business days, or (b) immediately upon
                            written notice, if Customer infringes upon or
                            misappropriates COMPANY’s intellectual property
                            rights, or if Customer becomes insolvent or ceases
                            to operate in the ordinary course, makes an
                            assignment for the benefit of creditors, or becomes
                            the subject of any bankruptcy, reorganization,
                            liquidation, dissolution, or similar proceeding; or
                            (c) immediately, without notice, for violation of
                            Applicable Law.
                        </p>
                        <p>
                            6.4 Effect of Termination. Upon termination or
                            expiration of this Agreement, Customer shall
                            immediately cease use of the Services and will
                            download its Customer End User data prior to such
                            termination or expiration. Following termination,
                            COMPANY shall have no obligation with respect to
                            storage of Customer Data and may, permanently delete
                            Customer Data in accordance with its internal
                            policies and procedures. Any termination or
                            expiration shall not relieve Customer of its
                            obligation to pay all Fees outlined in its
                            Chargeback Management Service Agreement(s).
                        </p>
                        <p>
                            6.5 Surviving Provisions. Upon any expiration or
                            termination of this Agreement, all rights,
                            obligations and licenses of the parties shall cease,
                            except for the provisions of Sections 1
                            (Definitions), 4.1 (Ownership), 4.2 (Feedback), 4.4
                            (Protection), 7.4 (Effect of Termination), 6.5
                            (Surviving Provisions), 7 (Fees and Payment Terms),
                            8 (Confidentiality), 9 (Indemnification), 10
                            (Limitation of Liability), 11 (General Provisions)
                            and any and all remedies for breach of this
                            Agreement shall survive any termination or
                            expiration.
                        </p>
                    </>
                ),
            },
            {
                id: 'fees',
                title: '7. Fees and Payment Terms',
                content: (
                    <>
                        <p>
                            7.1 Fees. Customer will pay all Fees specified in
                            the applicable Chargeback Management Services
                            Agreement(s) and shall provide COMPANY with accurate
                            and complete billing information and payment method.
                            Customer grants COMPANY and its service providers
                            permission to charge all Fees and any other fees
                            incurred for using the Services. The services will
                            be invoiced on a monthly basis with a payment term
                            of 7 days. Customer may, at any time, upgrade the
                            Subscription Services. After an upgrade, Customer
                            will be billed for additional Fees due under the
                            upgraded subscription immediately, which may co-term
                            with Customer’s current subscription. Except as
                            otherwise set forth in the Chargeback Management
                            Service Agreement(s), all invoices shall be due upon
                            receipt by Customers.
                        </p>
                        <p>
                            7.2 Late Fees; Acceleration. If any invoiced amount
                            is not received by COMPANY by the due date, then
                            without limiting COMPANY’s rights or remedies, the
                            overdue charges may accrue late interest at the rate
                            of 1.5% of the outstanding balance per month or the
                            maximum rate permitted by Applicable Law, whichever
                            is greater. If any amount owed by Customer under
                            this Agreement is more than thirty (30) days overdue
                            (or seven days for amounts charged to an authorized
                            credit card), COMPANY may, without limiting its
                            other rights and remedies, accelerate and require
                            immediate payment of Customer’s unpaid Fees and
                            suspend Services. With respect to declined credit
                            card and/or debit card payments, COMPANY will give
                            at least five days’ prior notice in accordance with
                            this Agreement that Customer’s account is delinquent
                            before suspending Services to Customer.
                        </p>
                        <p>
                            7.3 Taxes. The Fees and all other payments hereunder
                            do not include taxes, duties, tariffs, levies,
                            withholdings and similar assessments of any nature
                            (including without limitation, sales, use, and
                            value- added and withholding taxes), assessable by
                            any jurisdiction whatsoever (collectively, “Taxes”),
                            other than taxes based on COMPANY’s net income.
                            Customer is responsible for the payment of all Taxes
                            associated with its purchases hereunder. If COMPANY
                            has the legal obligation to pay or collect Taxes for
                            which Customer is responsible under this Agreement,
                            COMPANY will reflect such an amount on the invoice
                            unless Customer provides COMPANY with a valid tax
                            exemption certificate.
                        </p>
                    </>
                ),
            },
            {
                id: 'confidentiality',
                title: '8. Confidentiality',
                content: (
                    <>
                        <p>
                            8.1 Confidentiality Obligations. Each party will use
                            the other party’s Confidential Information solely as
                            reasonably required in furtherance of this
                            Agreement. Each Receiving Party will hold in
                            confidence the Disclosing Party’s Confidential
                            Information by means that are no less restrictive
                            than those used for its own confidential materials
                            (but not less than reasonable care); this includes
                            limiting access to the Disclosing Party’s
                            Confidential Information to Receiving Party
                            employees, contractors, or agents that have a need
                            to know the Disclosing Party’s Confidential
                            Information and that are subject to confidentiality
                            obligations that are consistent with those contained
                            herein. The obligations set forth herein will
                            survive for one (1) year after the termination or
                            expiration of this Agreement (except for
                            Confidential Information that is considered a trade
                            secret, which shall be subject to those
                            confidentiality obligations for so long as the
                            Confidential Information remains trade secret).
                        </p>
                        <p>
                            8.2 Exclusions. With the exception of Personal Data,
                            Confidential Information shall not include
                            information that: (i) is in, or enters, the public
                            domain without breach of this Agreement by the
                            Receiving Party, (ii) is legally known to by the
                            Receiving Party prior to disclosure by the
                            Disclosing Party, as demonstrated by supporting
                            documentation; (iii) is disclosed to the Receiving
                            Party by a third party under no obligation of
                            confidentiality and without violation of this
                            Agreement; or (iv) is developed independently by
                            receiver without the use or reference of the
                            Confidential Information.
                        </p>
                        <p>
                            8.3 Permissible Disclosure. The Receiving Party may
                            disclose Confidential Information to the extent
                            required by any Applicable Law, including court or
                            governmental order. Before making any such
                            disclosure, the Receiving Party shall, to the extent
                            permitted, provide the Disclosing Party reasonable
                            notice of such disclosure and will work with the
                            Disclosing Party, at the Disclosing Party’s expense,
                            in seeking confidential treatment, opposing
                            disclosure, seeking a protective order or other
                            similar protections for the Confidential
                            Information.
                        </p>
                    </>
                ),
            },
            {
                id: 'indemnification',
                title: '9. Indemnification',
                content: (
                    <>
                        <p>
                            9.1 Customer Indemnification. Subject to the
                            limitations set forth herein, Customer will defend
                            COMPANY, its Affiliates and each of their respective
                            directors, officers, employees, licensors and
                            authorized agents (collectively, “Indemnitees”) from
                            and against any Claims arising out of, resulting
                            from, or relating to (a) Customer Data, including
                            its transfer, processing and use as contemplated by
                            this Agreement; (b) Customer’s (or its Affiliate’s,
                            User’s, or End User’s) breach of this Agreement; (c)
                            Customer’s (or its Affiliate’s, User’s, or End
                            User’s) use of the Services in violation of third
                            party rights, including any intellectual property or
                            privacy rights or any Applicable Law; (d) Customer’s
                            (or its Affiliate’s, User’s, or End User’s) access
                            or use of the Services, Documentation, or
                            proprietary or intellectual property of COMPANY, and
                            shall indemnify and hold the Indemnitees harmless
                            from any and all resulting Losses.
                        </p>
                        <p>
                            9.2 Indemnification Procedure. COMPANY shall notify
                            Customer of any Claims for which it seeks
                            indemnification pursuant to Section 10.1. COMPANY
                            shall reasonably cooperate with Customer at
                            Customer’s sole cost and expense. Customer shall
                            immediately take control of the defense and
                            investigation of such Claim and shall employ counsel
                            approved by COMPANY, such approval not to be
                            unreasonably withheld, to defend the same, at
                            Customer’s sole cost and expense. COMPANY’s failure
                            to timely notify Customer under this Section 10.2
                            will not relieve Customer of its obligations under
                            this Section 10 except to the extent Customer is
                            materially prejudiced due to such failure. COMPANY
                            may participate in and observe the proceedings at
                            its own cost and expense with counsel of its own
                            choosing. Customer shall not settle any Claim
                            without COMPANY’s prior written consent.
                        </p>
                    </>
                ),
            },
            {
                id: 'liability',
                title: '10. Limitation of Liability',
                content: (
                    <>
                        <p>
                            TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW,
                            (I) IN NO EVENT WILL EITHER PARTY, ITS AFFILIATES,
                            DIRECTORS, EMPLOYEES, OR AGENTS HAVE ANY LIABILITY,
                            CONTINGENT OR OTHERWISE, FOR ANY SPECIAL,
                            CONSEQUENTIAL, PUNITIVE, STATUTORY OR EXEMPLARY
                            DAMAGES, EVEN IF THEY HAVE BEEN ADVISED OF THE
                            POSSIBILITY THEREOF, AND REGARDLESS OF THE LEGAL OR
                            EQUITABLE THEORY (CONTRACT, TORT, STATUTE, INDEMNITY
                            OR OTHERWISE) UPON WHICH ANY SUCH LIABILITY IS
                            BASED; AND (II) THE AGGREGATE LIABILITY OF COMPANY,
                            ITS AFFILIATES, DIRECTORS, EMPLOYEES, AND AGENTS
                            SHALL BE LIMITED TO TERMINATION OF THIS AGREEMENT
                            AND DAMAGES NOT TO EXCEED THE AMOUNTS PAID TO
                            COMPANY HEREUNDER WITH RESPECT TO THE SERVICES THAT
                            GAVE RISE TO THE CAUSE OF ACTION DURING THE
                            TWELVE-MONTH PERIOD PRIOR TO THE DATE THE CAUSE OF
                            ACTION AROSE, EVEN IF SUCH PARTY HAS BEEN ADVISED OF
                            THE POSSIBILITY OF SUCH DAMAGES. SOME JURISDICTIONS
                            DO NOT ALLOW THE EXCLUSION OF LIABILITY FOR
                            INCIDENTAL OR CONSEQUENTIAL DAMAGES OR PERSONAL
                            INJURY OR DEATH, SUCH THAT THE ABOVE LIMITATIONS MAY
                            NOT APPLY. IN THESE JURISDICTIONS, LIABILITY WILL BE
                            LIMITED TO THE GREATEST EXTENT PERMITTED BY
                            APPLICABLE LAW.
                        </p>
                    </>
                ),
            },
            {
                id: 'general',
                title: '11. General Provisions',
                content: (
                    <>
                        <p>
                            11.1 Export. The Services that COMPANY provides
                            under this Agreement are subject to export laws,
                            rules and regulations of the United States and other
                            jurisdictions. Customer warrants that it will comply
                            with all export laws, rules, and regulations in
                            regard to its export, import, and use of the
                            Services and represents that it (and its
                            representatives and Users) are not sanctioned or
                            prohibited from doing business with the United
                            States or any other applicable country. Customer
                            agrees to indemnify and hold COMPANY harmless from
                            any liability for Customer’s violation of applicable
                            export control laws.
                        </p>
                        <p>
                            11.2 Independent Contractors. The parties are
                            independent contractors under this Agreement, and
                            nothing herein will constitute either party as the
                            employer, employee, agent or representative of the
                            other party or both parties as joint venturers or
                            partners for any purpose.
                        </p>
                        <p>
                            11.3 Entire Agreement; Modifications. This Agreement
                            including the Data Processing Addendum, Acceptable
                            Use Policy, Chargeback Management Service
                            Agreement(s), (additional terms applicable to
                            specific Subscription Services, which supersede the
                            terms set forth herein as to the relevant
                            Subscription Services), and any agreement
                            incorporated herein by reference constitutes the
                            entire agreement of the parties with respect to the
                            subject matter hereof and supersedes all prior
                            negotiations, understandings or agreements between
                            the parties with respect to such subject matter. Any
                            terms contained in any other documentation provided
                            by Customer to COMPANY is void and will not become
                            part of the Agreement or bind the parties. In the
                            event of any conflict between the Chargeback
                            Management Service Agreement(s) and the Agreement,
                            the Agreement shall take precedence, unless as
                            expressly set forth in the Chargeback Management
                            Service Agreement(s). There are no representations,
                            agreements, arrangements, or understandings between
                            the parties relating to the subject matter of this
                            Agreement that are not fully expressed herein.
                            Customer agrees that its purchase is not contingent
                            on the delivery of any future functionality or
                            features. All waivers and modifications must be in
                            writing that both identifies itself as a waiver or
                            modification to this Agreement and is signed by both
                            parties.
                        </p>
                        <p>
                            11.4 Waiver. The parties may waive a breach of this
                            Agreement only by a writing executed by the party or
                            parties against whom the waiver is sought to be
                            enforced. No failure or delay (i) in exercising any
                            right or remedy, or (ii) in requiring the
                            satisfaction of any condition, (iii) under this
                            Agreement, and no act, omission or course of dealing
                            between the parties, operates as a waiver or
                            estoppel of any right, remedy or condition. A waiver
                            made in writing on one occasion is effective only in
                            that instance and only for the purpose stated. A
                            waiver once given is not to be construed as a waiver
                            on any future occasion or against any other party.
                        </p>
                        <p>
                            11.5 Severability. If any provision of this
                            Agreement is determined to be illegal or
                            unenforceable, that provision will be limited or
                            eliminated to the minimum extent necessary so that
                            this Agreement will otherwise remain in full force
                            and effect.
                        </p>
                        <p>
                            11.6 Choice of Law. This Agreement shall be governed
                            by English laws. Neither the United Nations
                            Convention on Contracts for the International Sale
                            of Goods nor any enactment of the Uniform Computer
                            Information Transactions Act shall apply to this
                            Agreement.
                        </p>
                        <p>
                            11.7 Force Majeure. Neither party is responsible for
                            nonperformance or delay in performance of its
                            obligations (other than payment of Fees) due to
                            causes, reason, event or circumstances beyond its
                            reasonable control, such as acts of God or nature,
                            strike, blockade, war, act of terrorism, riot,
                            Internet outages, failure of service providers
                            (including cloud service providers), failure or
                            diminishment of power or telecommunications or data
                            networks or services, pandemic, or refusal of a
                            license by a government agency, or any other cause,
                            reason, event or circumstances whether or not
                            similar to those listed in this Section 12.8.
                        </p>
                        <p>
                            11.8 Construction. Paragraph headings are for
                            convenience and shall have no effect on
                            interpretation. Any rule of construction to the
                            effect that ambiguities are to be resolved against
                            the drafting Party will not be applied in the
                            construction or interpretation of this Agreement.
                            The words “include” and “including” and variations
                            thereof, will not be deemed to be terms of
                            limitation, but deemed to be followed by the words
                            “without limitation.”
                        </p>
                        <p>
                            11.9 Third-Party Beneficiaries. There are no
                            third-party beneficiaries under this Agreement.
                        </p>
                        <p>
                            11.10 Counterparts. This Agreement may be executed
                            in counterparts, each of which shall be deemed to be
                            an original and which, taken together, shall be
                            deemed to constitute the entire Agreement and may be
                            used as evidence of the execution of the Agreement
                            to the same extent as that of an original signature.
                        </p>
                        <p>
                            11.11 Notices. Any notices or other communications
                            required in this Agreement (aside from billing
                            requests or questions) will be in English and in
                            writing and will be deemed to have been duly given
                            to a party upon receipt by email sent to
                            sdp.chargebacks@sabre.com, provided that any notices
                            of termination shall also be sent by email to
                            sdp.chargebacks@sabre.com with a copy to the
                            relevant assigned account manager. Notice to
                            CUSTOMER must be sent to the email address on the
                            most recent Chargeback Management Service
                            Agreement(s).
                        </p>
                        <p>
                            11.12 Controlling Language. This Agreement has been
                            prepared and executed in the English language only,
                            which language will be controlling in all respects.
                            Any translations of the provisions of this Agreement
                            into any other language are for reference only and
                            will have no legal or other effect. Any notice that
                            is required or permitted to be given by one party to
                            the other under this Agreement must be in the
                            English language and in writing. All proceedings
                            related to this Agreement will be conducted in the
                            English language.
                        </p>
                        <p>
                            11.13 Dispute Resolution. Prior to initiating any
                            claim or proceeding under, arising out of or in
                            connection with the Agreement, the parties will make
                            a good faith effort to resolve the underlying
                            dispute. Such good faith effort will include: (a)
                            elevating the issue to management personnel of each
                            party who have the power to settle the dispute on
                            behalf of that party within twenty (20) business
                            days; and (b) reasonably sharing relevant,
                            non‑privileged documents, books and records and
                            other materials, as reasonably requested, in
                            connection with the dispute. Customer agrees that
                            regardless of any statute or law to the contrary,
                            any claim or cause of action arising out of, related
                            to or connected with the use of the Services must be
                            filed within one year after such Claim arose.
                        </p>
                        <p>
                            11.14 Arbitration. For Claims outside of the United
                            States. If you reside outside the United States or
                            your business is located outside the United States,
                            you agree that the Members shall attempt to settle
                            any dispute or difference between or among them
                            amicably pursuant to Section 12.15. Should the
                            parties fail to reach a solution through such
                            negotiations, any Claim arising out of or in
                            connection with this Agreement, including any
                            question regarding its existence, validity,
                            implementation, interpretation or termination, shall
                            be determined by arbitration administered by e
                            London Court of International Arbitration (“LCIA”)
                            Rules (the “Rules”), which Rules are deemed to be
                            incorporated by reference into this clause. Within
                            ten (10) calendar days of service of a demand for
                            arbitration, the number of arbitrators shall be one
                            and appointed by mutual consent of the parties. In
                            case the parties disagree on the appointment for
                            more than forty-five (45) days after the
                            commencement of the arbitration, the sole arbitrator
                            will be appointed by the LCIA per the Rules. Any
                            arbitrator so appointed by the LCIA will be of a
                            nationality different than that of any party (the
                            “Arbitrator”). The parties agree that all
                            information concerning the fact, substance or result
                            of any such Dispute shall constitute Confidential
                            Information and shall not be disclosed except to the
                            extent necessary to enforce the arbitration award or
                            as otherwise required by Applicable Law. To the
                            fullest extent permitted by Applicable Law, no
                            arbitration proceeding under this Agreement shall be
                            joined to an arbitration involving any other party
                            subject to this Agreement, whether through class
                            action proceedings or otherwise. This provision
                            shall not prevent a party from seeking injunctive
                            relief from a Delaware federal or state court to
                            preserve the status quo or preserve evidence pending
                            arbitration.
                        </p>
                    </>
                ),
            },
            {
                id: 'product-terms',
                title: 'Product-Specific Terms',
                content: (
                    <>
                        <h4 style={{ marginTop: 0 }}>Case Builder</h4>
                        <p>
                            Payment Card Industry Data Security Standard (“PCI
                            DSS”) Compliance. Customer shall enter or upload
                            payment card number information only as labeled for
                            Input and is prohibited from entering full card
                            information in any other field. To the extent
                            Customer processes, stores, or transmits cardholder
                            data information using the Subscription Services,
                            the parties will comply with its responsibilities
                            under PCI DSS.
                        </p>
                        <h4>Transaction Dispute Service</h4>
                        <ol>
                            <li>
                                Customer is solely responsible for determining
                                the length of time it shall retain or store
                                (outside of COMPANY’s Services) downloaded
                                documents compiled by the Software Services, and
                                other records under any Applicable Law,
                                regulations, legal or administrative agency
                                processes.
                            </li>
                            <li>
                                Customer is solely responsible for providing
                                relevant data, or other documents with the
                                expressed knowledge that such Customer Data may
                                be designated to be transmitted as a function of
                                the Services, to third parties for the purpose
                                of resolving a Transaction Dispute with end
                                merchants, card networks or others, as and where
                                applicable; which may, at Customer’s discretion,
                                include Alternative Payment Method(s).
                            </li>
                            <li>
                                Certain laws or regulations may impose special
                                requirements with respect to Transaction
                                Disputes involving one or more “card account
                                holders,” such as the case with multiple
                                “bundled” Chargebacks, and Customer is solely
                                responsible for the outcome result of such
                                Transaction Disputes.
                            </li>
                            <li>
                                COMPANY does not and is not responsible to: (i)
                                determine whether any particular transaction was
                                properly authenticated prior to its original
                                initiation; (ii) furnish or obtain any
                                card-holder consents or determine if any such
                                consents have been withdrawn specifically
                                related to any request of a Transaction Dispute
                                refund request; (iii) provide any information or
                                disclosures in connection with any attempt to
                                obtain any such consents; (iv) provide legal
                                review of, or update or correct any information
                                provided by Customer, whether directly or
                                indirectly, to be exchanged with the appropriate
                                party for the purpose of raising a Chargeback or
                                resolving a Transaction Dispute; (v) provide any
                                such Customer Data copies to, or access to, the
                                actual end-user cardholder (consumer or
                                otherwise), except as expressly permitted
                                herein; or (vi) otherwise comply with any such
                                special requirements.
                            </li>
                        </ol>
                        <h4>Input and Output Features</h4>
                        <ol>
                            <li>
                                “Software” shall mean the web-based application
                                interface, referred to as the “chargeback
                                management web portal,” which may support
                                additional features and functionalities, based
                                on Customer needs and requirements, and which
                                minimally includes; data entry input methods
                                (i.e., Transaction Dispute webform) and document
                                upload features, and data collection APIs and
                                necessary connectivity method(s) to sufficiently
                                enable and maintain connectivity requirements
                                for Data Provider(s) and other Customer
                                authorized endpoints.
                            </li>
                            <li>
                                Customer hereby grants necessary permission and
                                authorization to Sabre, to facilitate the
                                exchange and transmission of Customer Data, as
                                needed and within the scope described herein, to
                                COMPANY, to deliver and provide the Services.
                            </li>
                            <li>
                                Subject to Customer’s compliance with the terms
                                and conditions of the Agreement, including
                                payment of all applicable fees, COMPANY hereby
                                grants to Customer for its internal business
                                purposes a limited, revocable, non-exclusive,
                                non-transferable, non-sublicensable right during
                                the Term to use, or permit Users to engage,
                                execute, download, consume, and use, the
                                Software for the purpose(s) and intent(s)
                                described herein.
                            </li>
                            <li>
                                Customer hereby grants necessary permission and
                                access to, Customer’s Payment Provider or Other
                                Data Provider (i.e., card network, sponsor bank,
                                merchant, etc.), as necessary and within the
                                scope described herein, to deliver and continue
                                to provide the Services. For payment services
                                not facilitated through Sabre Direct Pay or the
                                Sabre GDS, the Customer agrees to furnish
                                COMPANY with all necessary information,
                                documentation, or files required for the
                                provision of such services.
                            </li>
                        </ol>
                    </>
                ),
            },
        ],
        []
    )
    const containerRef = useRef<HTMLDivElement | null>(null)
    const outerContentRef = useRef<HTMLDivElement | null>(null)
    const sectionRefs = useMemo(
        () => new Map<string, HTMLDivElement | null>(),
        []
    )
    const [, setActiveSectionId] = useState(
        sections && sections.length > 0 ? sections[0].id : ''
    )
    const [pendingNavId, setPendingNavId] = useState<string | null>(null)
    // Buffer so section headers are visible below the modal title area
    const scrollOffsetPx = 112

    const handleScrollEvt = useCallback(
        (e?: React.UIEvent<HTMLDivElement>) => {
            const container =
                (e?.currentTarget as HTMLDivElement) ||
                containerRef.current ||
                outerContentRef.current
            if (!container || !sections || sections.length === 0) return
            // Highlight when the section header is ~25% down from the top of the visible area
            const y =
                container.scrollTop +
                scrollOffsetPx +
                container.clientHeight * 0.25
            const sorted = sections
                .map((s) => ({
                    id: s.id,
                    top: sectionRefs.get(s.id)?.offsetTop ?? 0,
                }))
                .sort((a, b) => a.top - b.top)
            let current = sorted[0]?.id
            for (let i = 0; i < sorted.length; i += 1) {
                if (sorted[i].top <= y) current = sorted[i].id
                else break
            }
            // If we are in a programmatic navigation, only update when
            // the target section becomes current to prevent flicker
            if (pendingNavId) {
                if (current === pendingNavId) {
                    setActiveSectionId(pendingNavId)
                    setPendingNavId(null)
                }
                return
            }
            if (current) setActiveSectionId(current)
        },
        [
            containerRef,
            outerContentRef,
            sections,
            sectionRefs,
            scrollOffsetPx,
            pendingNavId,
        ]
    )

    useEffect(() => {
        const handleResize = () => {
            handleScrollEvt()
        }

        window.addEventListener('resize', handleResize)
        // initialize
        handleResize()

        return () => {
            window.removeEventListener('resize', handleResize)
        }
    }, [handleScrollEvt])

    // Note: `showCloseIcon` prop is currently unused by design; keep for API compatibility

    const handleDeclineClick = async () => {
        try {
            const isSAMLBackDoor = localStorage.getItem('isSAMLBackDoor')
            await Auth.signOut()
            clearLocalStorage()
            const ssoTokenUrl = localStorage.getItem('SSOTokenURL') || ''
            if (ssoTokenUrl.length) {
                localStorage.removeItem('SSOTokenURL')
                window.location.href = ssoTokenUrl
                return
            }
            window.location.href = `/${
                isSAMLBackDoor ? '?saml_back_door_logout=1' : ''
            }`
        } catch (e) {
            clearLocalStorage()
            window.location.href = '/'
        }
    }

    interface TermsAcceptPayload {
        accepted: true
        acceptedAtIso: string
        username: string
        userId: string | number | null
        browser: {
            userAgent: string
            language: string
            platform: string
        }
    }

    const handleAcceptClick = () => {
        const payload: TermsAcceptPayload = {
            accepted: true,
            acceptedAtIso: new Date().toISOString(),
            username:
                ((user as unknown) as { username?: string })?.username ||
                ((user as unknown) as { email?: string })?.email ||
                ((user as unknown) as {
                    personRepresentation?: () => string
                })?.personRepresentation?.() ||
                'unknown',
            userId:
                ((user as unknown) as { id?: string | number })?.id ??
                // eslint-disable-next-line camelcase
                ((user as unknown) as { user_id?: string | number })?.user_id ??
                null,
            browser: {
                userAgent: navigator.userAgent,
                language: navigator.language,
                platform: navigator.platform,
            },
        }
        // TODO: remove this after testing
        // eslint-disable-next-line no-console
        console.info('T&C Accept payload', payload)
        handleAcceptTerms()
        setTosDisabled(true)
    }

    return (
        <Dialog
            fullWidth
            open={isTermsModalOpen && !tosDisabled}
            onClose={disableBackdropClick ? undefined : onClose}
            className={`${className} c-sabre-terms-and-conditions-modal`}
            maxWidth={maxModalWidth}
            id={`${idDirectory.divRoot}-${testId}`}
            scroll="body"
            PaperProps={{
                className: `c-sabre-terms-and-conditions-modal__paper ${paperClassName}`.trim(),
            }}
        >
            <DialogTitle
                className="c-sabre-terms-and-conditions-modal__title"
                id={`${idDirectory.divTitle}-${testId}`}
            >
                {headerTitle}
            </DialogTitle>
            <DialogContent
                className="c-sabre-terms-and-conditions-modal__content"
                id={`${idDirectory.divContent}-${testId}`}
                style={{ padding: 0 }}
                ref={outerContentRef}
                onScroll={handleScrollEvt}
            >
                <div
                    style={{
                        display: 'flex',
                        minHeight: 480,
                        maxHeight: 600,
                    }}
                >
                    <div
                        ref={containerRef}
                        onScroll={handleScrollEvt}
                        className="c-sabre-terms-and-conditions-modal__content-main"
                    >
                        {sections && sections.length > 0 ? (
                            <div>
                                {sections.map((s) => {
                                    const titleText = s.title
                                    return (
                                        <div
                                            key={s.id}
                                            ref={(el) =>
                                                sectionRefs.set(s.id, el)
                                            }
                                            data-section-id={s.id}
                                            style={{ marginBottom: 28 }}
                                        >
                                            <div
                                                style={{
                                                    fontSize: 18,
                                                    fontWeight: 700,
                                                    marginBottom: 8,
                                                }}
                                            >
                                                {titleText}
                                            </div>
                                            <div style={{ lineHeight: 1.6 }}>
                                                {s.content}
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        ) : (
                            <div style={{ lineHeight: 1.7 }}>{children}</div>
                        )}
                    </div>
                </div>
            </DialogContent>
            <DialogActions
                className="c-sabre-terms-and-conditions-modal__actions"
                id={`${idDirectory.divActions}-${testId}`}
            >
                <div className="c-sabre-terms-and-conditions-modal__actions-container">
                    {showDeclineBtn && (
                        <Button
                            variant="outlined"
                            onClick={() => {
                                onDecline?.()
                                handleDeclineClick()
                                setTosDisabled(false)
                            }}
                            id={`${idDirectory.btnSecondary}-${testId}`}
                            disabled={inactivateDeclineBtn}
                            sx={clientTheme.buttons?.defaultButton?.style}
                        >
                            {isLoadingDeclineBtn ? (
                                <>
                                    Loading... <CircularProgress size={16} />
                                </>
                            ) : (
                                declineBtnText
                            )}
                        </Button>
                    )}
                    <Button
                        variant="contained"
                        onClick={handleAcceptClick}
                        color="primary"
                        id={`${idDirectory.btnPrimary}-${testId}`}
                        disabled={inactivateAcceptBtn}
                    >
                        {isLoadingAcceptBtn ? (
                            <>
                                Submitting...{' '}
                                <CircularProgress
                                    className="emp-standardModal-circularProgress"
                                    color="secondary"
                                    size={16}
                                />
                            </>
                        ) : (
                            acceptBtnText
                        )}
                    </Button>
                </div>
            </DialogActions>
        </Dialog>
    )
}

export default SabreTermsAndConditionsModal
